import axios, { AxiosResponse } from 'axios';
import type { SalesStatus } from '../services/useMembershipPaymentsQueryService';
import { createDataObject } from '../lib/utils';

/**
 * 환불 멱등 키. 서버는 이 값으로 "네트워크 재시도"와 "같은 금액의 정당한 2번째 환불"을
 * 구분한다. 클라이언트가 알려주지 않으면 서버는 원리적으로 구분할 수 없다.
 *
 * 기존 PT/원매출 계약의 `operationId`를 유지한 채 `clientOperationId`를 함께 보낸다.
 * 두 이름 중 어느 쪽을 읽는 서버 버전에서도 같은 키로 동작한다.
 */
const toRefundIdempotencyFields = (operationId?: string) =>
    operationId ? { operationId, clientOperationId: operationId } : {};

const toIdempotencyHeader = (operationId?: string) =>
    operationId ? { 'Idempotency-Key': operationId } : {};

const toAccountingApiFailure = (error: any) => ({
    status: error?.response?.status ?? 0,
    data: error?.response?.data,
    retryable:
        typeof error?.response?.data?.details?.retryable === 'boolean'
            ? error.response.data.details.retryable
            : !error?.response || Number(error.response.status) >= 500,
    networkError: !error?.response,
});

export /**
 * @method get
 * @brief 관리자의 매출정산 기능 매출 목록 조회
 *  https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/MembershipPayment.md#%EB%9D%BC%ED%8F%AC%EB%A7%A4%EB%8B%88%EC%A0%80-%EB%A7%A4%EC%B6%9C%EC%A0%95%EC%82%B0-%EA%B4%80%EB%A0%A8-api%EB%93%A4
 */

const getSalesList = async ({
    startDate,
    endDate,
    userId,
    role,
    centerId,
    accessToken,
}: GetSalesListParams & { accessToken: string }): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/membershipPayments/salesList/${centerId}?startDate=${startDate}&endDate=${endDate}&userId=${userId}&role=${role}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};
export /**
 * @method post
 * @brief 관리자의 판매담당자 직접입력

 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%8C%90%EB%A7%A4%EB%8B%B4%EB%8B%B9%EC%9E%90-%EC%A7%81%EC%A0%91%EC%9E%85%EB%A0%A5
 */

const postSalesManager = async (membershipId: string, managerName: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/salesManagers/${membershipId}`,
            headers: { authorization: accessToken },
            data: {
                userName: managerName,
            },
        });

        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};
export /**
 * @method get
 * @brief 관리자의 판매담당자 조회
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%ED%8C%90%EB%A7%A4%EB%8B%B4%EB%8B%B9%EC%9E%90-%EC%A1%B0%ED%9A%8C
 */

const getSalesManagers = async (centerId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/salesManagers/${centerId}`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export type SalesManagerIdentity = {
    role: string;
    userId: string;
};

export type PatchSalesManagerParams = {
    membershipPaymentId: string;
    salesManager: SalesManagerIdentity;
    expectedSalesManager: SalesManagerIdentity | null;
    accessToken: string;
};

/**
 * 매출 원장 행의 판매 담당자를 정정한다.
 *
 * 호출부가 409 경합과 5xx/응답 유실을 구분하고, 후자의 경우 최신 매출을 재조회한 뒤
 * 사용자에게 다시 확인받아야 하므로 axios 오류를 삼키지 않고 status와 서버 data를 반환한다.
 */
export const SALES_MANAGER_PATCH_TIMEOUT_MS = 30000;

export const patchSalesManager = async ({
    membershipPaymentId,
    salesManager,
    expectedSalesManager,
    accessToken,
}: PatchSalesManagerParams): Promise<any> => {
    try {
        return await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/${membershipPaymentId}/sales-manager`,
            headers: { authorization: accessToken },
            data: { salesManager, expectedSalesManager },
            // 응답이 영영 안 오면 모달이 "요청 중"에 갇힌다(요청 중엔 닫기를 막는다). 끊기면 응답 유실로
            // 보고 다시 시도(재조회 → 재확인)로 보낸다 — 서버는 같은 요청 재전송에 수렴한다.
            timeout: SALES_MANAGER_PATCH_TIMEOUT_MS,
        });
    } catch (error: any) {
        return {
            status: error?.response?.status ?? 0,
            data: error?.response?.data,
            networkError: !error?.response,
        };
    }
};

export /**
 * @method patch
 * @brief 관리자의 수강권 환불
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%88%98%EA%B0%95%EA%B6%8C-%ED%99%98%EB%B6%88
 */

const patchMembershipRefund = async ({
    membershipId,
    salesRecordId,
    session,
    amount,
    date,
    source,
    centerId,
    isFullRefund,
    refundReason,
    operationId,
    accessToken,
}: PatchMembershipRefundAndUpgradedParams): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    // 매출 상세에서 시작한 환불은 원수강권/현재수강권 ID가 아니라 원매출 ID를 기준으로 처리한다.
    // 서버가 이 ID로 판매 담당자 스냅샷과 인계 계보를 멱등 operation으로 확정한다.
    if (salesRecordId) {
        try {
            response = await axios({
                method: 'post',
                url: `${process.env.REACT_APP_SERVER}/v1/membershipPayments/${salesRecordId}/refunds`,
                headers: {
                    authorization: accessToken,
                    ...toIdempotencyHeader(operationId),
                },
                data: {
                    amount,
                    session,
                    refundDate: date,
                    ...(refundReason ? { reason: refundReason } : {}),
                    ...toRefundIdempotencyFields(operationId),
                },
            });
            if (response.status === 200 || response.status === 201) return response;
        } catch (error: any) {
            return toAccountingApiFailure(error);
        }
        return response;
    }

    // 그룹 수강권: POST /v1/centers/:centerId/member-tickets/:id/refund-v2 (커밋 6bf11df5)
    if (source === 'group' && centerId) {
        try {
            response = await axios({
                method: 'post',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}/refund-v2`,
                headers: {
                    authorization: accessToken,
                    ...toIdempotencyHeader(operationId),
                },
                data: {
                    refundAmount: amount,
                    refundReason,
                    refundCountOffset: session,
                    isFullRefund: !!isFullRefund,
                    // 모달에서 선택한 환불 일자 — 미전송 시 서버가 오늘 날짜로 기록해 정산 귀속 월이 어긋남
                    refundDate: date,
                    // 부분 환불 재시도로 판매금액이 두 번 깎이는 것을 막는 키
                    ...toRefundIdempotencyFields(operationId),
                },
            });
            // PT는 200, 그룹은 200/201 가능 — 둘 다 성공 처리
            if (response.status === 200 || response.status === 201) {
                return response;
            }
        } catch (error: any) {
            return toAccountingApiFailure(error);
        }
        return response;
    }

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/refund/${membershipId}`,
            headers: {
                authorization: accessToken,
                ...toIdempotencyHeader(operationId),
            },
            data: {
                session,
                amount,
                date,
                ...toRefundIdempotencyFields(operationId),
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // 409/5xx/네트워크 오류도 구조화 본문과 함께 반환해 같은 operationId로 재시도한다.
        return toAccountingApiFailure(error);
    }
};
export /**
 * @method patch
 * @brief 관리자의 수강권 업그레이드
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1195/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%88%98%EA%B0%95%EA%B6%8C-%EC%97%85%EA%B7%B8%EB%A0%88%EC%9D%B4%EB%93%9C
 */

const patchMembershipUpgrade = async ({
    membershipId,
    session,
    amount,
    date,
    source,
    centerId,
    salesManager,
    operationId,
    accessToken,
}: PatchMembershipRefundAndUpgradedParams): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    // 그룹 업그레이드: POST /v1/centers/:centerId/member-tickets/:id/upgrade
    // PT와 동일 의미 — 기존 수강권에 회차/결제금액 추가 (서버가 PT 스타일로 변경 예정)
    // Body: { session, amount, date } — PT의 patchMembershipUpgrade와 완전 동일
    if (source === 'group' && centerId) {
        try {
            response = await axios({
                method: 'post',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}/upgrade`,
                headers: { authorization: accessToken },
                data: {
                    session,
                    amount,
                    date,
                },
            });
            if (response.status === 200 || response.status === 201) return response;
        } catch (error: any) {
            return toAccountingApiFailure(error);
        }
        return response;
    }

    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/upgrade/${membershipId}`,
            headers: {
                authorization: accessToken,
                'Idempotency-Key': operationId,
            },
            data: {
                session,
                amount,
                date,
                operationId,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        return toAccountingApiFailure(error);
    }
};

export /**
 * @method post
 * @brief 관리자의 수강권 분할 결제 처리
 * https://github.com/mustMoveProject/rappo-project/blob/RAPPO-1204/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%88%98%EA%B0%95%EA%B6%8C-%EB%B6%84%ED%95%A0-%EA%B2%B0%EC%A0%9C-%EC%B2%98%EB%A6%AC
 */

const postSplitPayment = async ({
    membershipId,
    amount,
    date,
    source,
    centerId,
    paymentMethod,
    salesManager,
    accessToken,
}: PostSplitPaymentParams): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;

    // 그룹 수강권: POST /v1/centers/:centerId/member-tickets/:id/split-payment (커밋 6bf11df5)
    if (source === 'group' && centerId) {
        try {
            response = await axios({
                method: 'post',
                url: `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-tickets/${membershipId}/split-payment`,
                headers: { authorization: accessToken },
                data: {
                    paymentAmount: amount,
                    paymentDate: date,
                    paymentMethod,
                    salesManager,
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response;
            }
        } catch (error: any) {
            // 상태만 돌려주면 409(경합·공유 보호)의 원인을 화면에서 알릴 수 없다.
            return toAccountingApiFailure(error);
        }
        return response;
    }

    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/membershippayments/splitpay/${membershipId}`,
            headers: { authorization: accessToken },
            data: {
                amount,
                date,
                // 판매담당자 — 백엔드가 아직 사용하지 않지만 전달 (추후 백엔드 확장 대비)
                ...(salesManager ? { salesManager } : {}),
            },
        });

        if (response.status === 201) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // PT 분할결제는 서버가 항상 lease로 직렬화해 경합 시 409(MEMBERSHIP_USAGE_BUSY /
        // SHARED_ACCOUNTING_OPERATION_IN_PROGRESS)가 난다. 분기 없는 상태코드를 삼키면
        // 수납이 기록되지 않았는데도 화면에 아무 안내가 남지 않는다.
        return toAccountingApiFailure(error);
    }
};
export /**
 *
 * @brief 관리자의 수강권 내역 수정
 * https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%88%98%EA%B0%95%EA%B6%8C-%EB%82%B4%EC%97%AD-%EC%88%98%EC%A0%95
 */
const patchMembershipPayment = async ({
    type,
    amount,
    date,
    membershipPaymentId,
    offset,
    source,
    centerId,
    accessToken,
}: PatchMembershipPaymentApiType): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    // PT: PATCH /v1/membershipPayments/update/:paymentId
    // 그룹: PATCH /v1/centers/:centerId/member-ticket-payments/:paymentId
    // 서버가 body shape을 통일해 offset/countOffset 둘 다 허용 (BACKEND_RESPONSE §3.4)
    // 그룹은 수정 시 MemberTicket.totalPrice/remainingCount/paymentDate를 자동 동기화함.
    const url =
        source === 'group' && centerId
            ? `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-ticket-payments/${membershipPaymentId}`
            : `${process.env.REACT_APP_SERVER}/v1/membershipPayments/update/${membershipPaymentId}`;
    const data = await createDataObject({ type, amount, date, offset });
    try {
        response = await axios({
            method: 'patch',
            url,
            headers: {
                authorization: accessToken,
            },
            data: data,
        });
        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 공유 중인 수강권의 결제 이력 수정은 409 SHARED_MEMBERSHIP_PAYMENT_HISTORY_MUTATION_BLOCKED로
        // 막힌다. 분기 없는 상태코드를 삼키면 버튼이 완전 무반응이 되어 관리자가 계속 다시 누른다.
        return toAccountingApiFailure(error);
    }
};
export /**
 *
 * @brief 관리자의 수강권 내역 삭제
 * https://github.com/mustMoveProject/rappo-project/blob/develop/src/docs/MembershipPayment.md#%EA%B4%80%EB%A6%AC%EC%9E%90%EC%9D%98-%EC%88%98%EA%B0%95%EA%B6%8C-%EB%82%B4%EC%97%AD-%EC%88%98%EC%A0%95
 */
const deleteMembershipPayment = async ({
    membershipPaymentId,
    source,
    centerId,
    accessToken,
}: {
    membershipPaymentId: string;
    source?: 'pt' | 'group';
    centerId?: string;
    accessToken: string;
}): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    // 그룹 수강권 결제 내역 삭제: DELETE /v1/centers/:centerId/member-ticket-payments/:paymentId
    const url =
        source === 'group' && centerId
            ? `${process.env.REACT_APP_SERVER}/v1/centers/${centerId}/member-ticket-payments/${membershipPaymentId}`
            : `${process.env.REACT_APP_SERVER}/v1/membershipPayments/delete/${membershipPaymentId}`;
    try {
        response = await axios({
            method: 'delete',
            url,
            headers: {
                authorization: accessToken,
            },
        });
        // PT: 204, 그룹: 200
        if (response.status === 204 || response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        // 수정과 같은 이유로 삭제도 공유 중이면 409로 막힌다. 상태와 본문을 항상 넘긴다.
        return toAccountingApiFailure(error);
    }
};
export type GetSalesListParams = {
    startDate: string;
    endDate: string;
    userId: string | 'all';
    role: 'coach' | 'etc' | 'all';
    centerId: string;
};

/**
 * PT와 그룹 수강권 공용 파라미터.
 * - PT: PATCH /v1/membershippayments/refund|upgrade/:id — session, amount, date
 * - Group (source='group'+centerId): POST /v1/centers/:centerId/member-tickets/:id/refund-v2 또는 /upgrade
 *   프론트에서 동일 params 받되 내부에서 엔드포인트/body 변환
 */
export type PatchMembershipRefundAndUpgradedParams = {
    membershipId: string;
    /** 매출 상세 환불 전용. 값이 있으면 원매출 기반 환불 API를 사용한다. */
    salesRecordId?: string;
    session: number;
    amount: number;
    date: string;
    accessToken: string;
    /**
     * 멱등 키. body(operationId + clientOperationId)와 Idempotency-Key 헤더에 같은 값을 보낸다.
     * 환불은 PT·그룹·원매출 세 경로 모두 사용하고, 업그레이드는 PT만 사용한다.
     */
    operationId?: string;
    /** 'group'이면 MemberTicket 전용 엔드포인트로 분기 */
    source?: 'pt' | 'group';
    /** source='group'일 때 필수 */
    centerId?: string;
    /** 그룹 환불 전용 — 미래 예약 일괄 취소 + status REFUNDED */
    isFullRefund?: boolean;
    /** 그룹 환불 전용 */
    refundReason?: string;
    /** 그룹 업그레이드/환불 공용 — 판매담당자 */
    salesManager?: { role: string; userId: string; userName: string };
};

export type PostSplitPaymentParams = {
    membershipId: string;
    amount: number;
    date: string;
    accessToken: string;
    /** 'group'이면 MemberTicket 전용 엔드포인트 */
    source?: 'pt' | 'group';
    centerId?: string;
    paymentMethod?: string;
    salesManager?: { role: string; userId: string; userName: string };
};

export type PatchMembershipPaymentApiType = {
    // "type": "PARTIAL_PAYMENT", // 'PARTIAL_REFUND' 부분환불 | 'FULL_REFUND' 전체환불 | 'UPGRADED' 업그레이드 | 'PARTIAL_PAYMENT' 분할결제
    // "amount": 99000,
    // "date": "2025-01-23T15:00:00.000+00:00"
    type: SalesStatus;
    amount: number;
    date: string;
    offset?: number;
    membershipPaymentId: string;
    accessToken: string;
    /** 'group'이면 MemberTicketPayment 전용 엔드포인트로 분기 */
    source?: 'pt' | 'group';
    centerId?: string;
};
